import React from 'react'
import Datetime from 'react-datetime'
import moment from 'moment'
import { KTSVG } from '_metronic/helpers'

import type {
  CustomerGrade,
  ProductionFactoriesState,
  ProductionOrderItem,
  ProductionOrderRequestFormState,
  ResponsibilityOwner,
} from '../../Detail/Tabs/ProductionOrderRequest/types'

interface Props {
  roleId?: number
  isView?: boolean
  value: ProductionOrderRequestFormState
  onChange: <K extends keyof ProductionOrderRequestFormState>(
    key: K,
    val: ProductionOrderRequestFormState[K]
  ) => void
  onFactoryChange: (
    key: keyof ProductionFactoriesState,
    patch: Partial<ProductionFactoriesState[keyof ProductionFactoriesState]>
  ) => void
  onDocumentChange: (
    key: keyof ProductionOrderRequestFormState['documents'],
    val: boolean | string
  ) => void
  onResponsibilityChange: (rowIndex: number, owner: ResponsibilityOwner) => void
  onCustomerGradeChange: (grade: CustomerGrade) => void
  addItemRow: () => void
  removeItemRow: (id: string) => void
  updateItemRow: (id: string, field: keyof Omit<ProductionOrderItem, 'id'>, value: string) => void
}

const RESPONSIBILITY_GROUP_SOURCE: { title: string; rows: string[] }[] = [
  {
    title: 'ระบบสาธารณูปโภค',
    rows: ['ไฟฟ้าแคมป์', 'ไฟฟ้าเชื่อม', 'ไฟฟ้า Grout', 'ประปาแคมป์', 'ประปา Grout'],
  },
  {
    title: 'สิ่งอำนวยความสะดวก',
    rows: ['ห้องน้ำ', 'ที่พัก'],
  },
  {
    title: 'การเตรียมพื้นที่',
    rows: ['วางหมุด', 'ถนนทางเข้า', 'ตัดต้นไม้', 'เคลียร์ผัง'],
  },
  {
    title: 'การจัดการภายนอกและอื่นๆ',
    rows: ['ยกสายสื่อสาร', 'จัดการจราจร', 'ความรับผิดชอบปัญหาข้างเคียง'],
  },
]

let responsibilityRowCounter = 0
const RESPONSIBILITY_GROUPS_INDEXED = RESPONSIBILITY_GROUP_SOURCE.map(group => ({
  title: group.title,
  rows: group.rows.map(label => ({
    label,
    rowIndex: responsibilityRowCounter++,
  })),
}))

const CUSTOMER_GRADE_OPTIONS = [
  { id: 'survey-tab1-grade-aplus', value: 'A_PLUS' as const, label: 'A+' },
  { id: 'survey-tab1-grade-a', value: 'A' as const, label: 'A' },
  { id: 'survey-tab1-grade-b', value: 'B' as const, label: 'B' },
  { id: 'survey-tab1-grade-c', value: 'C' as const, label: 'C' },
  { id: 'survey-tab1-grade-grey', value: 'GREY_LIST' as const, label: 'Grey List' },
  { id: 'survey-tab1-grade-black', value: 'BLACK_LIST' as const, label: 'Black List' },
]

const FACTORY_ROWS: {
  key: keyof ProductionFactoriesState
  id: string
  label: string
}[] = [
  { key: 'factory1', id: 'survey-tab1-prod-f1', label: 'โรงงาน 1' },
  { key: 'factory2', id: 'survey-tab1-prod-f2', label: 'โรงงาน 2' },
  { key: 'factory3', id: 'survey-tab1-prod-f3', label: 'โรงงาน 3' },
  { key: 'joint', id: 'survey-tab1-prod-joint', label: 'โรงร่วม' },
]

type ItemSectionColumnKey =
  | 'no'
  | 'item'
  | 'quantity'
  | 'pricePerQ'
  | 'pilePerUnit'
  | 'drivingPerUnit'
  | 'other'
  | 'pileAndDrivingPerUnit'

const ITEM_SECTION_COLUMNS: { key: ItemSectionColumnKey; label: string }[] = [
  { key: 'no', label: 'ที่' },
  { key: 'item', label: 'รายการ' },
  { key: 'quantity', label: 'จำนวน' },
  { key: 'pricePerQ', label: 'ราคา/คิว' },
  { key: 'pilePerUnit', label: 'เสา/ต้น' },
  { key: 'drivingPerUnit', label: 'ตอก/ต้น' },
  { key: 'other', label: 'อื่นๆ' },
  { key: 'pileAndDrivingPerUnit', label: 'เสา+ตอก/ต้น' },
]

const SurveyTab1AdditionalSections: React.FC<Props> = ({
  roleId = 0,
  isView,
  value,
  onChange,
  onFactoryChange,
  onDocumentChange,
  onResponsibilityChange,
  onCustomerGradeChange,
  addItemRow,
  removeItemRow,
  updateItemRow,
}) => {
  const canSeePriceColumns = roleId === 1 || roleId === 7
  const itemColumns = React.useMemo(
    () =>
      ITEM_SECTION_COLUMNS.filter(col =>
        canSeePriceColumns ? true : ['no', 'item', 'quantity'].includes(col.key)
      ),
    [canSeePriceColumns]
  )

  const getOwner = (rowIndex: number): ResponsibilityOwner =>
    value.responsibility.find(r => r.rowIndex === rowIndex)?.owner ?? null

  return (
    <>
      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <div className='card-title fw-bold'>รายการ</div>
            {!isView && (
              <div className='card-toolbar'>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary fw-bold'
                  onClick={addItemRow}>
                  เพิ่มแถว
                </button>
              </div>
            )}
          </div>
          <div className='card-body'>
            <div className='table-responsive'>
              <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-bold text-gray-800'>
                    {itemColumns.map(col => (
                      <th key={col.key} className={col.key === 'no' ? 'w-50px text-center' : ''}>
                        {col.label}
                      </th>
                    ))}
                    {!isView && <th className='w-50px' />}
                  </tr>
                </thead>
                <tbody>
                  {value.items.map((row, index) => (
                    <tr key={row.id}>
                      {itemColumns.map(col => {
                        if (col.key === 'no') {
                          return (
                            <td key={col.key}>
                              <input
                                type='text'
                                className='form-control form-control-sm text-center'
                                disabled={isView}
                                value={row.no}
                                placeholder={String(index + 1)}
                                onChange={e => updateItemRow(row.id, 'no', e.target.value)}
                              />
                            </td>
                          )
                        }
                        const fieldKey = col.key
                        return (
                          <td key={fieldKey}>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              disabled={isView}
                              value={row[fieldKey]}
                              onChange={e => updateItemRow(row.id, fieldKey, e.target.value)}
                            />
                          </td>
                        )
                      })}
                      {!isView && (
                        <td className='text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-icon btn-light-danger'
                            onClick={() => removeItemRow(row.id)}
                            disabled={value.items.length <= 1}>
                            <KTSVG
                              path='/media/icons/duotune/general/gen027.svg'
                              className='svg-icon-1'
                            />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <div className='card-title fw-bold'>คำร้องขอพิเศษในแผนงาน</div>
          </div>
          <div className='card-body'>
            <div className='row gy-4'>
              <div className='col-12'>
                <div className='d-flex flex-wrap align-items-center gap-3'>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='survey-tab1-special-pillar'
                      checked={value.specialPillarEnabled}
                      disabled={isView}
                      onChange={e => onChange('specialPillarEnabled', e.target.checked)}
                    />
                    <label
                      className='form-check-label text-gray-800'
                      htmlFor='survey-tab1-special-pillar'>
                      เสาฤกษ์ / เสาเอก วันที่
                    </label>
                  </div>
                  <div style={{ maxWidth: 220 }}>
                    <Datetime
                      locale='en'
                      dateFormat='DD/MM/YYYY'
                      timeFormat={false}
                      value={value.specialPillarDate ?? undefined}
                      onChange={(val: any) => {
                        if (moment.isMoment(val) || val instanceof Date) {
                          onChange('specialPillarDate', moment(val).toDate())
                        }
                      }}
                      renderInput={props => (
                        <input
                          {...props}
                          autoComplete='off'
                          className='form-control form-control-sm'
                          disabled={isView}
                          aria-label='เสาฤกษ์ / เสาเอก วันที่'
                        />
                      )}
                      closeOnClickOutside
                      className='filter-date-input w-100 z-4'
                    />
                  </div>
                </div>
              </div>
              <div className='col-12'>
                <div className='d-flex flex-wrap align-items-center gap-3'>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='survey-tab1-special-stop'
                      checked={value.specialStopEnabled}
                      disabled={isView}
                      onChange={e => onChange('specialStopEnabled', e.target.checked)}
                    />
                    <label className='form-check-label text-gray-800' htmlFor='survey-tab1-special-stop'>
                      หยุด (H) ระหว่าง S3-G1 ครั้งที่
                    </label>
                  </div>
                  <span className='text-gray-700'>#1</span>
                  <input
                    type='text'
                    inputMode='numeric'
                    className='form-control form-control-sm'
                    style={{ width: 100 }}
                    value={value.specialStopCount1}
                    disabled={isView}
                    aria-label='ครั้งที่ 1'
                    onChange={e => onChange('specialStopCount1', e.target.value)}
                  />
                  <span className='text-gray-700'>#2</span>
                  <input
                    type='text'
                    inputMode='numeric'
                    className='form-control form-control-sm'
                    style={{ width: 100 }}
                    value={value.specialStopCount2}
                    disabled={isView}
                    aria-label='ครั้งที่ 2'
                    onChange={e => onChange('specialStopCount2', e.target.value)}
                  />
                </div>
              </div>
              <div className='col-12'>
                <div className='form-check mb-2'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='survey-tab1-special-other'
                    checked={value.specialOtherEnabled}
                    disabled={isView}
                    onChange={e => onChange('specialOtherEnabled', e.target.checked)}
                  />
                  <label className='form-check-label text-gray-800' htmlFor='survey-tab1-special-other'>
                    อื่นๆ ระบุ
                  </label>
                </div>
                <textarea
                  className='form-control form-control-sm'
                  rows={4}
                  value={value.specialOtherDetail}
                  disabled={isView}
                  onChange={e => onChange('specialOtherDetail', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <div className='card-title fw-bold'>การผลิต</div>
          </div>
          <div className='card-body'>
            <div className='row gy-4'>
              {FACTORY_ROWS.map(row => (
                <div key={row.id} className='col-12'>
                  <div className='d-flex flex-wrap align-items-center gap-3'>
                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id={row.id}
                        checked={value.productionFactories[row.key].enabled}
                        disabled={isView}
                        onChange={e => onFactoryChange(row.key, { enabled: e.target.checked })}
                      />
                      <label className='form-check-label text-gray-800' htmlFor={row.id}>
                        {row.label}
                      </label>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <input
                        type='text'
                        inputMode='decimal'
                        className='form-control form-control-sm text-end'
                        style={{ width: 88 }}
                        value={value.productionFactories[row.key].percent}
                        disabled={isView}
                        aria-label={`${row.label} เปอร์เซ็นต์`}
                        onChange={e => onFactoryChange(row.key, { percent: e.target.value })}
                      />
                      <span className='text-gray-700'>%</span>
                    </div>
                  </div>
                </div>
              ))}
              <div className='col-12'>
                <label className='form-label mb-2' htmlFor='survey-tab1-prod-company'>
                  จากบริษัท
                </label>
                <input
                  id='survey-tab1-prod-company'
                  type='text'
                  className='form-control form-control-sm'
                  value={value.productionCompany}
                  disabled={isView}
                  onChange={e => onChange('productionCompany', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <div className='card-title fw-bold'>เอกสารแนบ</div>
          </div>
          <div className='card-body'>
            <div className='row gy-3'>
              {[
                { key: 'soil' as const, id: 'survey-tab1-doc-soil', label: 'ผลเจาะดิน' },
                { key: 'site' as const, id: 'survey-tab1-doc-site', label: 'ใบดูที่' },
                { key: 'contract' as const, id: 'survey-tab1-doc-contract', label: 'สัญญาซื้อขาย' },
                { key: 'cert' as const, id: 'survey-tab1-doc-cert', label: 'หนังสือรับรอง' },
                { key: 'vat' as const, id: 'survey-tab1-doc-vat', label: 'บริษัท / ภพ.20' },
              ].map(item => (
                <div key={item.id} className='col-12'>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id={item.id}
                      checked={value.documents[item.key]}
                      disabled={isView}
                      onChange={e => onDocumentChange(item.key, e.target.checked)}
                    />
                    <label className='form-check-label text-gray-800' htmlFor={item.id}>
                      {item.label}
                    </label>
                  </div>
                </div>
              ))}
              <div className='col-12'>
                <div className='d-flex flex-wrap align-items-center gap-3'>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='survey-tab1-doc-other'
                      checked={value.documents.other}
                      disabled={isView}
                      onChange={e => onDocumentChange('other', e.target.checked)}
                    />
                    <label className='form-check-label text-gray-800' htmlFor='survey-tab1-doc-other'>
                      อื่นๆ ระบุ
                    </label>
                  </div>
                  <input
                    type='text'
                    className='form-control form-control-sm flex-grow-1'
                    style={{ minWidth: 200 }}
                    value={value.documents.otherDetail}
                    disabled={isView}
                    onChange={e => onDocumentChange('otherDetail', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <div className='card-title fw-bold'>เกรดลูกค้า</div>
          </div>
          <div className='card-body'>
            <div className='d-flex flex-wrap gap-5'>
              {CUSTOMER_GRADE_OPTIONS.map(opt => (
                <div key={opt.id} className='form-check'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='survey-tab1-customer-grade'
                    id={opt.id}
                    value={opt.value}
                    checked={value.customerGrade === opt.value}
                    disabled={isView}
                    onChange={() => onCustomerGradeChange(opt.value)}
                  />
                  <label className='form-check-label text-gray-800' htmlFor={opt.id}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <div className='card-title fw-bold'>ข้อมูลทางการเงิน</div>
          </div>
          <div className='card-body'>
            <div className='row gy-5'>
              <div className='col-12'>
                <div className='fw-bold mb-3'>การเงิน มัดจำ</div>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  <input
                    type='text'
                    inputMode='decimal'
                    className='form-control form-control-sm text-end'
                    style={{ width: 72 }}
                    value={value.depositPercent}
                    disabled={isView}
                    aria-label='มัดจำ เปอร์เซ็นต์'
                    onChange={e => onChange('depositPercent', e.target.value)}
                  />
                  <span>%</span>
                  <span className='mx-1'>=</span>
                  <input
                    type='text'
                    inputMode='decimal'
                    className='form-control form-control-sm'
                    style={{ minWidth: 160, maxWidth: 280 }}
                    value={value.depositAmount}
                    disabled={isView}
                    aria-label='จำนวนเงินมัดจำ'
                    onChange={e => onChange('depositAmount', e.target.value)}
                  />
                  <span>บาท</span>
                </div>
              </div>
              <div className='col-12'>
                <label className='form-label mb-2' htmlFor='survey-tab1-finance-note'>
                  ข้อมูลทางการเงิน / งบการเงิน
                </label>
                <textarea
                  id='survey-tab1-finance-note'
                  className='form-control form-control-sm'
                  rows={6}
                  value={value.financeNote}
                  disabled={isView}
                  onChange={e => onChange('financeNote', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <div className='card-title fw-bold'>ความรับผิดชอบ</div>
          </div>
          <div className='card-body'>
            <div className='table-responsive'>
              <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-bold text-gray-800'>
                    <th className='min-w-200px'>รายการ</th>
                    <th className='min-w-100px text-center'>PACO</th>
                    <th className='min-w-100px text-center'>ลูกค้า</th>
                  </tr>
                </thead>
                <tbody>
                  {RESPONSIBILITY_GROUPS_INDEXED.map(group => (
                    <React.Fragment key={group.title}>
                      <tr>
                        <td colSpan={3} className='bg-light-primary fw-bold fs-11 p-4'>
                          {group.title}
                        </td>
                      </tr>
                      {group.rows.map(({ label, rowIndex: idx }) => {
                        const owner = getOwner(idx)
                        const pacoId = `survey-tab1-resp-${idx}-paco`
                        const custId = `survey-tab1-resp-${idx}-customer`
                        return (
                          <tr key={`${group.title}-${label}`}>
                            <td className='px-4'>{label}</td>
                            <td className='text-center'>
                              <input
                                className='form-check-input border-dark'
                                type='radio'
                                id={pacoId}
                                name={`survey-tab1-resp-${idx}-owner`}
                                checked={owner === 'PACO'}
                                style={{ accentColor: '#000000', borderColor: '#000000' }}
                                disabled={isView}
                                aria-label={`${label} PACO`}
                                onChange={() => onResponsibilityChange(idx, 'PACO')}
                              />
                            </td>
                            <td className='text-center'>
                              <input
                                className='form-check-input border-dark'
                                type='radio'
                                id={custId}
                                name={`survey-tab1-resp-${idx}-owner`}
                                checked={owner === 'CUSTOMER'}
                                style={{ accentColor: '#000000', borderColor: '#000000' }}
                                disabled={isView}
                                aria-label={`${label} ลูกค้า`}
                                onChange={() => onResponsibilityChange(idx, 'CUSTOMER')}
                              />
                            </td>
                          </tr>
                        )
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SurveyTab1AdditionalSections
